import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { allNews } from "@/lib/media";

const PAGE_SIZE = 4;

export default function NewsPage() {
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: "影视资讯 - 91蜜桃",
    description:
      "91蜜桃影视资讯频道，汇集最新影片动态、剧集观察、行业分析与年度盘点，帮助你及时了解影视圈的大小事。",
    keywords: "91蜜桃资讯,影视资讯,影视动态,行业观察,影片动态,剧集观察",
  });

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return allNews;
    return allNews.filter((item) =>
      [item.title, item.summary, item.category].join(" ").toLowerCase().includes(q),
    );
  }, [keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <Breadcrumb items={[{ label: "资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            影视资讯
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
            关注影视行业的最新动态、口碑变化与内容趋势，用最短的时间了解正在发生的事。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-bold text-foreground-900">{filtered.length}</span> 条资讯
          </p>
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2 focus-within:border-primary-400 sm:w-72">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="筛选资讯标题"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setPage(1);
                }}
                aria-label="清空"
                className="text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="space-y-4">
              {pageItems.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex animate-fade-up flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-4 shadow-sm transition-all duration-300 hover:shadow-md sm:flex-row md:p-5"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <div className="h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-52">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 91蜜桃影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                        {item.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-calendar-line" />
                        {item.date}
                      </span>
                    </div>
                    <h2 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                      {item.title}
                    </h2>
                    <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                      {item.summary}
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                      阅读全文
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination current={safePage} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-newspaper-line"
            title="没有找到相关资讯"
            desc="换个关键词试试，或者浏览全部资讯内容。"
            actionLabel="查看全部资讯"
            onAction={() => {
              setKeyword("");
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}